import { TABLE_LAYOUT_TOKENS } from "@shared/styles/tableLayoutTokens";

export const MY_PROFILE_CONSTANTS = {
  TITLE: "My Profile",
  PROFILE_INFORMATION_TITLE: "PROFILE INFORMATION",
  PROFILE_INFORMATION_SUBTITLE:
    "Use this section to update your personal contact information. Updates here will not update the address on your Investor Account.",
  EDIT_PROFILE_BUTTON: "Edit Profile",
  SAVE_CHANGES_BUTTON: "Save Changes",
  CANCEL_BUTTON: "Cancel",
  DISCARD_CHANGES_BUTTON: "Discard Changes",
  RESET_PASSWORD_BUTTON: "RESET PASSWORD",
  RESET_MFA_BUTTON: "RESET MFA METHOD",
  ACCOUNT_FUND_ACCESS_TITLE: "ACCOUNT / FUND ACCESS",
  ACCOUNT_FUND_ACCESS_SUBTITLE: "This section shows your current account and fund access.",
  REQUEST_CONTACT_UPDATE_BUTTON: "Request Contact Update",
  EDIT_BUTTON: "EDIT",
  ABOUT_PERMISSIONS_LINK: "About Permissions",
  GROUP_EMAIL_CHECKBOX_LABEL: "Is this a group email?",
  GROUP_EMAIL_TOOLTIP: "Select group email if multiple users will access LP Connect from this email address",
  GROUP_EMAIL_DEFAULT_LAST_NAME: "Team",
} as const;

export * from "./validationMessages";
export * from "./aboutPermissionsContent";

export const PROFILE_FIELD_LABELS = {
  FIRST_NAME: "First Name",
  MIDDLE_NAME: "Middle Name",
  LAST_NAME: "Last Name",
  EMAIL: "Email",
  GROUP_NAME: "Group Name",
  GROUP_EMAIL: "Group Email",
  CONTACT_NUMBER: "Contact Number",
  COUNTRY: "Country",
  REGISTERED_ADDRESS: "Contact Address",
  STATE_PROVINCE: "State / Province",
  CITY: "City",
  POSTAL_CODE: "Postal Code",
} as const;

/** MUI Grid sizes for Profile Information fields — 5 columns desktop / 2 tablet / 1 mobile (LPC-10868). */
export const PROFILE_FIELD_GRID = {
  /** Mobile: 1 field per row */
  xs: 12,
  /** Tablet: 2 fields per row */
  sm: 6,
  md: 6,
  /** Desktop: 5 fields per row (12 ÷ 2.4 = 5) */
  lg: 2.4,
} as const;

/**
 * Group-email mode special row (LPC-10868): when group email is selected, the first row stays as
 * 3 fields on desktop — Group Name + Group Email + Contact Number — to show group email replaced the
 * First/Middle/Last name fields. Desktop = 3 per row (lg 4); breakpoints fall back to 2 (sm/md) / 1 (xs).
 * The remaining fields keep the normal 5/2/1 rule (PROFILE_FIELD_GRID).
 */
export const PROFILE_GROUP_EMAIL_GRID = {
  xs: 12,
  sm: 6,
  md: 6,
  lg: 4,
} as const;

export const PROFILE_FIELD_MAX_LENGTHS = {
  FIRST_NAME: 40,
  MIDDLE_NAME: 25,
  LAST_NAME: 80,
  GROUP_NAME: 40,
  CONTACT_NUMBER: 40,
  REGISTERED_ADDRESS: 200,
  CITY: 100,
  STATE_PROVINCE: 100,
  POSTAL_CODE: 20,
  EMAIL: 80,
  GROUP_EMAIL: 80,
} as const;

// Color for highlighting pending email field
export const PENDING_HIGHLIGHT_COLOR = "#039BE5";

// Color used for the destructive "Discard Changes" button (not in MUI theme palette)
export const PROFILE_DISCARD_COLOR = "#E03F3F";

// Email change status labels for reconciliation
export const EMAIL_CHANGE_STATUS = {
  PENDING: "Pending",
  APPROVED: "Approved",
  REJECTED: "Rejected",
  CLOSED: "Closed",
} as const;

export const PROFILE_TOOLTIPS = {
  CONTACT_NUMBER:
    "This does not update your MFA phone number. To change your MFA method, please use the Reset MFA Method below.",
  REGISTERED_ADDRESS:
    "This address is for your personal profile only. To update your registered Investor Account address, contact ",
  INVESTOR_SERVICES_EMAIL: "investor.services@carlyle.com",
  REQUEST_CONTACT_UPDATE:
    "Request changes to fund access, including adding, removing, or updating access for yourself or a contact you manage.",
} as const;

export const ACCOUNT_FUND_ACCESS_LABELS = {
  ACCOUNTS: "ACCOUNTS",
  FUNDS: "FUNDS",
  ROLES: "ROLES",
  DOCUMENT_ACCESS: "DOCUMENT ACCESS",
  NOTIFICATIONS_FILTER: "NOTIFICATIONS",
  NOTIFICATIONS: "Notification",
  STATUS: "STATUS",
  ALL_ACCOUNTS: "All Accounts",
  ALL_FUNDS: "All Funds",
  ALL_ROLES: "All Roles",
  ALL_DOCUMENT_ACCESS: "All Document Access",
  ALL_NOTIFICATIONS: "All Notifications",
  ALL_STATUSES: "All Statuses",
  ACCOUNT_COLUMN: "Account",
  FUND_COLUMN: "Fund",
  ROLE_COLUMN: "Role",
  DOCUMENT_ACCESS_COLUMN: "Document Access",
  NOTIFICATION_COLUMN: "Notifications",
  STATUS_COLUMN: "Status",
  // LPC-10834 AC2: action column appears only in Edit View
  ACTION_COLUMN: "Action",
  // LPC-10827 AC3/AC4: clicking the trash stages the row for removal and replaces the
  // trash with a single-line underlined "Stop removal" link (see STOP_REMOVAL below).
  ROLE_SELECT_ARIA_LABEL: "Select role for this fund",
  LEARN_MORE_LABEL: "Learn about roles, document access and notifications.",
  SHOW_MORE: "More",
  SHOW_LESS: "Less",
  NO_NOTIFICATIONS: "No notifications",
  ROWS_PER_PAGE: TABLE_LAYOUT_TOKENS.PAGE_SIZE,
  ABOUT_PERMISSIONS_TOOLTIP:
    "Learn how contact permissions work, including roles, document access, and notification settings.",
  CLEAR_FILTERS: "Clear Filters",
  STOP_REMOVAL: "Stop removal",
  REMOVE_AVAILABLE_ACCESS_TOOLTIP: "Remove available access",
  REMOVE_AVAILABLE_ACCESS_ARIA: "Remove available access for this fund",
  STOP_REMOVAL_ARIA: "Stop removal for this fund",
  UNDO_ROW_TOOLTIP: "Undo changes for this row",
  UNDO_ROW_ARIA: "Undo permission changes for this fund",
} as const;

export const ACCOUNT_FUND_ACCESS_TAG_STYLES = {
  OUTLINE_COLOR: "#98A2B3",
  // Used for tag text alignment inside chips ("left aligned" = flex-start)
  LABEL_JUSTIFY_CONTENT: "flex-start",
  PENDING_REMOVAL_BG: "#FBE7E7",
  ACTION_COLUMN_WIDTH: 100,
} as const;

export const DOCUMENT_ACCESS_OPTIONS = [
  { id: "Capital Call", label: "Capital Calls" },
  { id: "Distribution", label: "Distributions" },
  { id: "Financial Reporting", label: "Financial Documents" },
  { id: "Legal Information", label: "Legal Documents" },
  { id: "Tax Information", label: "Tax Documents" },
] as const;

export const NOTIFICATION_OPTIONS = [
  { id: "Capital Call Management Notification", label: "Capital Calls" },
  { id: "Distribution Management Notification", label: "Distributions" },
  { id: "Financial Reporting Notification", label: "Financial Documents" },
  { id: "Legal Information Notification", label: "Legal Documents" },
  { id: "Tax Information Notification", label: "Tax Documents" },
] as const;

// Lookup map: API notification name → display label (used by grid to show friendly names)
export const NOTIFICATION_LABEL_MAP: Record<string, string> = Object.fromEntries(
  NOTIFICATION_OPTIONS.map((opt) => [opt.id, opt.label])
);

export const STATUS_OPTIONS = [
  { id: "In Review", label: "In Review" },
  { id: "Removal Pending", label: "Removal Pending" },
] as const;

// Role type constants
export const ROLE_TYPES = {
  PRIMARY_CONTACT: "Primary Contact",
  SECONDARY_CONTACT: "Secondary Contact",
  TAX_ADVISOR: "Tax Advisor",
  LEGAL_ADVISOR: "Legal Advisor",
  FINANCE_CONTACT: "Finance Contact",
  AML_KYC_CONTACT: "AML/KYC Contact",
  WIRE_APPROVER: "Wire Approver",
} as const;

// Role display order (for sorting roles in the UI)
export const ROLE_ORDER: string[] = [
  "Primary Contact",
  "Secondary Contact",
  "Tax Advisor",
  "Legal Advisor",
  "Finance Contact",
  "AML/KYC Contact",
  "Wire Approver",
];

// Internal funds only support 3 roles
export const INTERNAL_FUND_ROLE_ORDER: string[] = ["Primary Contact", "Secondary Contact", "Tax Advisor"];

// Roles a contact can be reassigned to via the in-grid editable Role select (applies to internal
// and external/hybrid rows alike).
export const SELECTABLE_CONTACT_ROLES: string[] = ["Primary Contact", "Secondary Contact", "Tax Advisor"];

// Role color mapping - hex codes for each role type
export const ROLE_COLORS: Record<string, string> = {
  "Primary Contact": "#1D6571",
  "Secondary Contact": "#2198AC",
  "Wire Approver": "#5F5F5F",
  "AML/KYC Contact": "#EBEBEB",
  "Finance Contact": "#C5F9BA",
  "Legal Advisor": "#F2E8D3",
  "Tax Advisor": "#B6E7FA",
} as const;
